import React from "react"
import { AutocompleteRenderOptionState, Stack, Typography } from "@mui/material"
import { WithTooltip } from "../../../../../ready-shared-components/components"
import Checkbox from "../../../Checkbox"
import { AutocompleteProps } from "../../../Autocomplete"
import { IndentationDropdownOptions } from "../../types"
import { useStyles } from "./styles"

type Props = React.HTMLAttributes<HTMLLIElement> & {
  option: IndentationDropdownOptions
  state: AutocompleteRenderOptionState
  withCheckbox?: boolean
  withTooltip?: boolean
  hideIndentationMarkers?: boolean
  getMarker?: (option: IndentationDropdownOptions) => React.ReactNode
  noWrap?: boolean
  classes?: AutocompleteProps["classes"]
  disabled?: boolean
  labelClassName?: string
}

const getDefaultMarker = (option: IndentationDropdownOptions) => {
  return new Array(option.level ?? 0).fill("-").join("")
}

const TOOLTIP_PROPS = {
  arrow: true,
  disableInteractive: true
}

const IndentationDropdownItem: React.FC<Props> = ({
  option,
  state,
  hideIndentationMarkers,
  withCheckbox,
  withTooltip,
  noWrap,
  getMarker = getDefaultMarker,
  classes: outerClasses,
  disabled: disabledProps,
  labelClassName,
  ...props
}) => {
  const { classes, cx } = useStyles({ hideIndentationMarkers })

  const indentationMarker = getMarker(option)

  const { selected } = state
  const disabled = !!props?.["aria-disabled"] || disabledProps

  return (
    <Stack
      {...props}
      key={option.value}
      component="li"
      aria-selected={selected}
      className={cx(classes.item, outerClasses?.item, {
        [classes.selectedItem]: selected,
        [classes.disabledItem]: disabled,
        [classes.selectedDisabledItem]: selected && disabled
      })}
      onClick={!disabled ? props.onClick : () => {}}
      onMouseMove={!disabled ? props.onMouseMove : () => {}}
      onTouchStart={!disabled ? props.onTouchStart : () => {}}
    >
      {indentationMarker && !option.inputValue && (
        <Typography className={classes.level} variant="body2">
          {indentationMarker}
        </Typography>
      )}
      {withCheckbox && !option.inputValue && !disabledProps && (
        <Checkbox
          disableRipple
          className={cx(classes.checkbox, outerClasses?.checkbox)}
          checked={selected}
          // a11y: the option row conveys name + selected state; hide the
          // decorative checkbox so AT doesn't announce a nameless one (SC 4.1.2)
          aria-hidden
          inputProps={{ tabIndex: -1 }}
        />
      )}
      <WithTooltip
        tooltip={option.label}
        showTooltip={withTooltip}
        tooltipProps={TOOLTIP_PROPS}
      >
        <Typography variant="body2" noWrap={noWrap} className={labelClassName}>
          {option.label}
        </Typography>
      </WithTooltip>
    </Stack>
  )
}

export default IndentationDropdownItem
